import { useEffect, useMemo, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { cn } from '@/lib/utils.ts'

import { CheckboxWithLabel } from '@/components/CheckboxWithLabel.tsx'
import { LabelManagementModal } from '@/components/LabelManagementModal.tsx'
import { GroupedStoryPanels } from '@/components/panels/StoryPanels.tsx'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import { BlockedStoriesView, useBlockedStoriesSummary } from '@/components/views/BlockedStoriesView.tsx'
import { LabelStoriesView, LabelSummary } from '@/components/views/LabelStoriesView.tsx'
import { SearchStoriesView, SearchSummary } from '@/components/views/SearchStoriesView.tsx'
import { VIEW_MODES } from '@/constants/VIEW_MODES.ts'
import { StoryInteractionProvider } from '@/contexts/StoryInteractionContext.tsx'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { useUserStorage } from '@/hooks/useUserStorage.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'

export const ProjectPage = () => {
  const [viewMode, setViewMode] = useState<PanelViewMode>(VIEW_MODES.DEFAULT)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const [showMoveError, setShowMoveError] = useState(false)
  const [filter, setFilter] = useState<SearchFilter | null>(null)

  const { value: panelVisibility, storeValue: setPanelVisibility } = useUserStorage<PanelVisibilityState>(
    'selectedPanels',
    defaultVisibilityState,
  )

  const handleCheckChange = (panel: keyof PanelVisibilityState, checked: boolean) =>
    setPanelVisibility({ ...panelVisibility, [panel]: checked })

  const { blockedStoryCount } = useBlockedStoriesSummary()

  useSubscription([
    [
      GlobalPubSub.Signals.SELECT_LABEL,
      ((selectedLabel: string | null) => {
        setSelectedLabel(selectedLabel)
        setViewMode(selectedLabel ? VIEW_MODES.LABEL : VIEW_MODES.DEFAULT)
      }) as Callback,
    ],
    [
      GlobalPubSub.Signals.SEARCH,
      ((filter: SearchFilter) => {
        setViewMode(filter ? VIEW_MODES.SEARCH : VIEW_MODES.DEFAULT)
        setTimeout(() => setFilter(filter))
      }) as Callback,
    ],
    [
      GlobalPubSub.Signals.DISPLAY_STORY_MOVE_ERROR,
      ((showMoveError: boolean) => setShowMoveError(showMoveError)) as Callback,
    ],
  ])

  useEffect(() => {
    if (selectedLabel && viewMode === VIEW_MODES.LABEL) return
    setSelectedLabel(null)
  }, [viewMode, selectedLabel])

  const { panels, summary, isShowClearButton } = useMemo(() => {
    switch (true) {
      case viewMode === VIEW_MODES.BLOCKER:
        return {
          panels: (
            <BlockedStoriesView
              isShowDone={false}
              isShowRelevant={panelVisibility.isShowRelevant}
              isShowUnscheduled={panelVisibility.isShowUnscheduled}
            />
          ),
        }

      case selectedLabel && viewMode === VIEW_MODES.LABEL:
        return {
          panels: (
            <LabelStoriesView
              selectedLabel={selectedLabel}
              isShowDone={panelVisibility.isShowDone}
              isShowRelevant={panelVisibility.isShowRelevant}
              isShowUnscheduled={panelVisibility.isShowUnscheduled}
            />
          ),
          summary: <LabelSummary selectedLabel={selectedLabel} />,
          isShowClearButton: true,
        }

      case viewMode === VIEW_MODES.SEARCH:
        return {
          panels: (
            <SearchStoriesView
              filter={filter}
              isShowDone={panelVisibility.isShowDone}
              isShowRelevant={panelVisibility.isShowRelevant}
              isShowUnscheduled={panelVisibility.isShowUnscheduled}
            />
          ),
          summary: <SearchSummary filter={filter} />,
          isShowClearButton: true,
        }

      default:
        return {
          panels: (
            <GroupedStoryPanels
              isShowDone={panelVisibility.isShowDone}
              isShowRelevant={panelVisibility.isShowRelevant}
              isShowUnscheduled={panelVisibility.isShowUnscheduled}
            />
          ),
        }
    }
  }, [viewMode, panelVisibility, selectedLabel, filter])

  return (
    <div data-testid="current-iteration-backlog" className="p-4 h-full">
      <div className="flex flex-col h-full">
        <div className="flex justify-between flex-grow-0">
          <div className="flex flex-row items-center gap-2">
            <div className="flex gap-1">
              <CheckboxWithLabel
                variant="primary"
                id="done_checkbox"
                labelText="Done"
                checked={panelVisibility.isShowDone}
                onCheckChange={(checked) => handleCheckChange('isShowDone', checked as boolean)}
                disabled={viewMode === VIEW_MODES.BLOCKER}
              />
              <CheckboxWithLabel
                variant="primary"
                id="relevant_checkbox"
                labelText="Current/Backlog"
                checked={panelVisibility.isShowRelevant}
                onCheckChange={(checked) => handleCheckChange('isShowRelevant', checked as boolean)}
              />
              <CheckboxWithLabel
                variant="primary"
                id="unscheduled_checkbox"
                labelText="Icebox"
                checked={panelVisibility.isShowUnscheduled}
                onCheckChange={(checked) => handleCheckChange('isShowUnscheduled', checked as boolean)}
              />
            </div>
            {summary}
            {isShowClearButton && (
              <ClearButton
                onClick={() => {
                  if (viewMode === VIEW_MODES.SEARCH) {
                    setFilter(null)
                    GlobalPubSub.publish(GlobalPubSub.Signals.RESET_SEARCH)
                  }
                  setViewMode(VIEW_MODES.DEFAULT)
                }}
              />
            )}
          </div>
          <div className="flex items-center">
            <div className="flex gap-2 items-center">
              <Button
                type="button"
                variant="ghost"
                className={cn(
                  'p-2 h-8 flex items-center gap-1 rounded-sm font-EN-KR-body2-regular text-contents-tertiary bg-interactive-inactive',
                  'hover:bg-interactive-inactive hover:text-contents-tertiary',
                  {
                    'text-contents-onDarkPrimary bg-interactive-primary': viewMode === VIEW_MODES.BLOCKER,
                  },
                )}
                onClick={() => {
                  const blockerViewModeActivated = viewMode === VIEW_MODES.BLOCKER
                  setViewMode(blockerViewModeActivated ? VIEW_MODES.DEFAULT : VIEW_MODES.BLOCKER)
                }}
              >
                {viewMode === VIEW_MODES.BLOCKER ? (
                  <img src="/images/blocker-active-view-icon.svg" alt="blocker view" />
                ) : (
                  <img src="/images/blocker-inactive-view-icon.svg" alt="blocker view" />
                )}
                {`Blocked${blockedStoryCount > 0 ? ` (${blockedStoryCount})` : ''}`}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className={cn(
                  'p-2 h-8 flex items-center gap-1 rounded-sm font-EN-KR-body2-regular text-contents-tertiary bg-interactive-inactive',
                  'hover:bg-interactive-inactive hover:text-contents-tertiary',
                )}
                onClick={() => GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_LABEL_MANAGEMENT_MODAL, true)}
              >
                <img src="/images/label-inactive-view-icon.svg" alt="label view" />
                Labels
              </Button>
            </div>
          </div>
        </div>
        <StoryInteractionProvider isSortable={viewMode === VIEW_MODES.DEFAULT}>
          <div className="sm:w-full w-min flex-1 flex gap-2 mt-2 overflow-y-auto">{panels}</div>
        </StoryInteractionProvider>
        <LabelManagementModal />

        <Dialog open={showMoveError} onOpenChange={setShowMoveError}>
          <DialogContent className="p-4 gap-6 [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="font-EN-KR-sub-heading-bold text-contents-primary">
                Please add an estimate first!
              </DialogTitle>
              <DialogDescription className="font-EN-KR-body1-regular text-contents-primary">
                You cannot move unestimated stories to the backlog.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowMoveError(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

type ClearButtonProps = {
  onClick: () => void
}

const ClearButton = ({ onClick }: ClearButtonProps) => {
  return (
    <Button variant="secondary" size="sm" className="rounded-xs py-1 px-1 gap-0 h-auto" onClick={onClick}>
      <span className="font-EN-KR-caption-bold px-0.5 text-contents-onDarkPrimary whitespace-nowrap">Clear</span>
      <img src="/images/x.svg" alt="clear" />
    </Button>
  )
}

type PanelVisibilityState = {
  isShowDone: boolean
  isShowRelevant: boolean
  isShowUnscheduled: boolean
}

const defaultVisibilityState: PanelVisibilityState = {
  isShowDone: true,
  isShowRelevant: true,
  isShowUnscheduled: true,
}
